import { ExtrudeGeometry } from 'three';
import { Part } from './types';
import { computeTransforms, buildBendGeometry } from './geometry';
import { buildFlangeShape } from './flangeShape';
import { findActiveMiterPairs } from './miterPairs';

// STEP AP214 writer (ISO 10303-21). Outputs a triangulated MANIFOLD_SOLID_BREP
// with shared vertices and edges between adjacent triangles, so Solid Edge,
// SolidWorks, Fusion, Inventor, Onshape, FreeCAD, etc. import it as a true
// parametric solid body (not a mesh / convergent body).
//
// In Solid Edge: File → Open → choose .stp → opens as Part. To convert to a
// proper sheet-metal part, use Application Button → Convert to Sheet Metal
// (or the Recognize Sheet Metal command in the Sheet Metal environment).
//
// We emit one ADVANCED_FACE per triangle with shared EDGE_CURVEs, which keeps
// the topology manifold (every edge belongs to exactly two faces) so SE
// accepts it as a closed shell. Adjacent coplanar triangles will appear as
// separate faces — heal them in SE with "Combine Faces" if needed.

interface Triangle {
  v0: [number, number, number];
  v1: [number, number, number];
  v2: [number, number, number];
}

function collectTriangles(part: Part): Triangle[] {
  const tris: Triangle[] = [];
  const transforms = computeTransforms(part);
  const miterPairs = findActiveMiterPairs(part);
  const T = part.thickness;

  const drain = (g: { getAttribute: any; getIndex: any }) => {
    const pos = g.getAttribute('position');
    if (!pos) return;
    const idx = g.getIndex?.();
    const at = (i: number): [number, number, number] => [pos.getX(i), pos.getY(i), pos.getZ(i)];
    if (idx) {
      for (let i = 0; i + 2 < idx.count; i += 3) {
        tris.push({ v0: at(idx.getX(i)), v1: at(idx.getX(i + 1)), v2: at(idx.getX(i + 2)) });
      }
    } else {
      for (let i = 0; i + 2 < pos.count; i += 3) {
        tris.push({ v0: at(i), v1: at(i + 1), v2: at(i + 2) });
      }
    }
  };

  for (const id of Object.keys(part.flanges)) {
    const xf = transforms[id];
    if (!xf) continue;
    const shape = buildFlangeShape(part, id, miterPairs);
    const g = new ExtrudeGeometry(shape, { depth: T, bevelEnabled: false });
    g.translate(0, 0, -T / 2);
    g.applyMatrix4(xf.matrix);
    drain(g);
  }
  for (const id of Object.keys(part.flanges)) {
    const f = part.flanges[id];
    if (!f.attachment) continue;
    const parentT = transforms[f.attachment.parentId];
    if (!parentT) continue;
    const g = buildBendGeometry(part, f.attachment.parentId, id, parentT);
    if (g) drain(g);
  }
  return tris;
}

// STEP requires real numbers to carry a decimal point ("1.", not "1"). This
// formatter emits enough precision for sub-micron CAD work and strips trailing
// zeros without dropping the decimal marker.
function fmt(n: number): string {
  if (!isFinite(n) || Math.abs(n) < 1e-10) return '0.';
  let s = n.toFixed(7);
  if (s.indexOf('.') >= 0) {
    s = s.replace(/0+$/, '');
    if (s.endsWith('.')) s += '0';
  }
  return s;
}

class StepWriter {
  private entities: string[] = [];
  private nextId = 1;
  private vertexCache = new Map<string, number>();
  private edgeCache = new Map<string, number>();

  private emit(body: string): number {
    const id = this.nextId++;
    this.entities.push(`#${id} = ${body};`);
    return id;
  }

  private cartesianPoint(x: number, y: number, z: number): number {
    return this.emit(`CARTESIAN_POINT('',(${fmt(x)},${fmt(y)},${fmt(z)}))`);
  }

  private direction(x: number, y: number, z: number): number {
    return this.emit(`DIRECTION('',(${fmt(x)},${fmt(y)},${fmt(z)}))`);
  }

  private vertexPoint(p: [number, number, number]): number {
    // Coordinate-based dedup so triangles sharing a corner reuse the same
    // VERTEX_POINT, which is what STEP requires for a manifold shell.
    const key = `${p[0].toFixed(5)},${p[1].toFixed(5)},${p[2].toFixed(5)}`;
    const cached = this.vertexCache.get(key);
    if (cached !== undefined) return cached;
    const ptId = this.cartesianPoint(p[0], p[1], p[2]);
    const vId = this.emit(`VERTEX_POINT('',#${ptId})`);
    this.vertexCache.set(key, vId);
    return vId;
  }

  // Return the EDGE_CURVE id for the line between two vertices, plus a flag
  // telling the caller whether their (v1→v2) direction matches the stored
  // edge orientation (so they can set ORIENTED_EDGE.orientation correctly).
  private edgeCurve(
    v1: number, v2: number,
    p1: [number, number, number], p2: [number, number, number],
  ): { id: number; forward: boolean } {
    const a = Math.min(v1, v2), b = Math.max(v1, v2);
    const forward = v1 === a;
    const key = `${a}<${b}`;
    const cached = this.edgeCache.get(key);
    if (cached !== undefined) return { id: cached, forward };

    const low = forward ? p1 : p2;
    const high = forward ? p2 : p1;
    const dx = high[0] - low[0], dy = high[1] - low[1], dz = high[2] - low[2];
    const len = Math.hypot(dx, dy, dz);
    if (len < 1e-10) return { id: -1, forward };
    const dirId = this.direction(dx / len, dy / len, dz / len);
    const startPtId = this.cartesianPoint(low[0], low[1], low[2]);
    const vecId = this.emit(`VECTOR('',#${dirId},1.)`);
    const lineId = this.emit(`LINE('',#${startPtId},#${vecId})`);
    const ecId = this.emit(`EDGE_CURVE('',#${a},#${b},#${lineId},.T.)`);
    this.edgeCache.set(key, ecId);
    return { id: ecId, forward };
  }

  private triangleFace(t: Triangle): number {
    const v0 = this.vertexPoint(t.v0);
    const v1 = this.vertexPoint(t.v1);
    const v2 = this.vertexPoint(t.v2);
    if (v0 === v1 || v1 === v2 || v2 === v0) return -1;

    const e01 = this.edgeCurve(v0, v1, t.v0, t.v1);
    const e12 = this.edgeCurve(v1, v2, t.v1, t.v2);
    const e20 = this.edgeCurve(v2, v0, t.v2, t.v0);
    if (e01.id < 0 || e12.id < 0 || e20.id < 0) return -1;

    const oe1 = this.emit(`ORIENTED_EDGE('',*,*,#${e01.id},${e01.forward ? '.T.' : '.F.'})`);
    const oe2 = this.emit(`ORIENTED_EDGE('',*,*,#${e12.id},${e12.forward ? '.T.' : '.F.'})`);
    const oe3 = this.emit(`ORIENTED_EDGE('',*,*,#${e20.id},${e20.forward ? '.T.' : '.F.'})`);
    const loopId = this.emit(`EDGE_LOOP('',(#${oe1},#${oe2},#${oe3}))`);
    const bndId = this.emit(`FACE_OUTER_BOUND('',#${loopId},.T.)`);

    // Plane: outward normal from CCW triangle (right-hand rule). X-axis along
    // the first edge — STEP needs an axis2_placement with origin + Z (normal)
    // + X (ref). The face normal must agree with the loop orientation, which
    // is why we pass .T. on the FACE_OUTER_BOUND and ADVANCED_FACE here.
    const ux = t.v1[0] - t.v0[0], uy = t.v1[1] - t.v0[1], uz = t.v1[2] - t.v0[2];
    const vx = t.v2[0] - t.v0[0], vy = t.v2[1] - t.v0[1], vz = t.v2[2] - t.v0[2];
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const nLen = Math.hypot(nx, ny, nz);
    if (nLen < 1e-12) return -1;
    nx /= nLen; ny /= nLen; nz /= nLen;
    const uLen = Math.hypot(ux, uy, uz);
    if (uLen < 1e-12) return -1;
    const xx = ux / uLen, xy = uy / uLen, xz = uz / uLen;

    const originId = this.cartesianPoint(t.v0[0], t.v0[1], t.v0[2]);
    const normalDirId = this.direction(nx, ny, nz);
    const xDirId = this.direction(xx, xy, xz);
    const axisId = this.emit(`AXIS2_PLACEMENT_3D('',#${originId},#${normalDirId},#${xDirId})`);
    const planeId = this.emit(`PLANE('',#${axisId})`);
    return this.emit(`ADVANCED_FACE('',(#${bndId}),#${planeId},.T.)`);
  }

  build(triangles: Triangle[]): string {
    const faceIds: number[] = [];
    for (const t of triangles) {
      const fid = this.triangleFace(t);
      if (fid > 0) faceIds.push(fid);
    }
    if (faceIds.length === 0) {
      // Even an empty part needs a valid file structure; emit a minimal stub.
      faceIds.push(this.emit(`ADVANCED_FACE('empty',(),#${this.emit(`PLANE('',#${this.emit(
        `AXIS2_PLACEMENT_3D('',#${this.cartesianPoint(0, 0, 0)},#${this.direction(0, 0, 1)},#${this.direction(1, 0, 0)})`
      )})`)},.T.)`));
    }

    const shellId = this.emit(`CLOSED_SHELL('',(${faceIds.map((i) => '#' + i).join(',')}))`);
    const brepId = this.emit(`MANIFOLD_SOLID_BREP('Body',#${shellId})`);

    // Units + context (mm, radians)
    const lenUnitId = this.emit(`( LENGTH_UNIT() NAMED_UNIT(*) SI_UNIT(.MILLI.,.METRE.) )`);
    const angleUnitId = this.emit(`( NAMED_UNIT(*) PLANE_ANGLE_UNIT() SI_UNIT($,.RADIAN.) )`);
    const solidAngleUnitId = this.emit(`( NAMED_UNIT(*) SI_UNIT($,.STERADIAN.) SOLID_ANGLE_UNIT() )`);
    const uncId = this.emit(`UNCERTAINTY_MEASURE_WITH_UNIT(LENGTH_MEASURE(1.E-7),#${lenUnitId},'distance_accuracy_value','confusion accuracy')`);
    const ctxId = this.emit(`( GEOMETRIC_REPRESENTATION_CONTEXT(3) GLOBAL_UNCERTAINTY_ASSIGNED_CONTEXT((#${uncId})) GLOBAL_UNIT_ASSIGNED_CONTEXT((#${lenUnitId},#${angleUnitId},#${solidAngleUnitId})) REPRESENTATION_CONTEXT('Context #1','3D Context with UNIT and UNCERTAINTY') )`);

    // Product / shape hierarchy required by AP214
    const appCtxId = this.emit(`APPLICATION_CONTEXT('core data for automotive mechanical design processes')`);
    this.emit(`APPLICATION_PROTOCOL_DEFINITION('international standard','automotive_design',2000,#${appCtxId})`);
    const prodCtxId = this.emit(`PRODUCT_CONTEXT('part definition',#${appCtxId},'mechanical')`);
    const prodId = this.emit(`PRODUCT('BendSteelCal','BendSteelCal','',(#${prodCtxId}))`);
    const formId = this.emit(`PRODUCT_DEFINITION_FORMATION('','',#${prodId})`);
    const defCtxId = this.emit(`PRODUCT_DEFINITION_CONTEXT('part definition',#${appCtxId},'design')`);
    const prodDefId = this.emit(`PRODUCT_DEFINITION('design','',#${formId},#${defCtxId})`);
    const prodShapeId = this.emit(`PRODUCT_DEFINITION_SHAPE('','',#${prodDefId})`);
    const shapeRepId = this.emit(`ADVANCED_BREP_SHAPE_REPRESENTATION('',(#${brepId}),#${ctxId})`);
    this.emit(`SHAPE_DEFINITION_REPRESENTATION(#${prodShapeId},#${shapeRepId})`);

    const ts = new Date().toISOString().replace(/\.\d+Z$/, '');
    const header = [
      `ISO-10303-21;`,
      `HEADER;`,
      `FILE_DESCRIPTION(('BendSteelCal STEP export — sheet-metal part'),'2;1');`,
      `FILE_NAME('part.stp','${ts}',('BendSteelCal user'),('Sapsricharoen Engineering'),'BendSteelCal web exporter','BendSteelCal','');`,
      `FILE_SCHEMA(('AUTOMOTIVE_DESIGN { 1 0 10303 214 1 1 1 1 }'));`,
      `ENDSEC;`,
      `DATA;`,
    ].join('\n');
    const footer = `ENDSEC;\nEND-ISO-10303-21;\n`;
    return `${header}\n${this.entities.join('\n')}\n${footer}`;
  }
}

export function exportStep(part: Part): string {
  const tris = collectTriangles(part);
  return new StepWriter().build(tris);
}

export function downloadStep(filename: string, content: string) {
  const blob = new Blob([content], { type: 'application/step' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
